import React, { Component } from 'react'
import { connect } from 'react-redux'
import { withRouter } from 'common/utils/withRouter'
import _ from 'lodash'
import classNames from 'classnames'
import { ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import BigBreadcrumbs from '../../../components/layout/navigation/components/BigBreadcrumbs'
import WidgetGrid from '../../../components/layout/widgets/WidgetGrid'
import JarvisWidget from '../../../components/layout/widgets/JarvisWidget'
import Datatable from '../../../components/tables/Datatable'
import { createStatusBadgeString } from './StatusBadge'
import {
  fetchDiamonds,
  clearDiamonds,
  clearSelectedDiamond
} from '../../../common/actions/diamond'
import { getRetailer } from '../../../common/actions/retailer'
import { isStandardRetailer } from '../../../common/utils/retailer'
import {
  statusCodes,
  jewelryTypes,
  settings
} from '../../../common/utils/diamond'
import imageUrl from '../../../common/utils/imageUrl'
import { showSavedMessage } from '../../../common/utils/savedMessage'
import { LIST_PAGE_LENGTH, SERVER } from '../../constants'
import UpgradeAccountButton from '../common/UpgradeAccountButton'
import ClearFormFieldButton from '../common/ClearFormFieldButton'
import EmptyTableDiamonds from './EmptyTableDiamonds'
import defaultDiamondImage from '../../../assets/images/image-coming-soon.jpg'
import styles from './List.module.css'

const isNA = value =>
  value == null || value === '' || value === 'not-applicable'
const renderCarat = carat => (isNA(carat) || carat <= 0 ? 'N/A' : carat)
const renderTitle = title =>
  isNA(title) ? 'N/A' : String(title).split('-').map(_.capitalize).join(' ')
const renderClarity = clarity =>
  isNA(clarity) ? 'N/A' : String(clarity).replace(/-/g, ' ').toUpperCase()
const renderUpper = value => (isNA(value) ? 'N/A' : _.toUpper(value))
const renderColor = value =>
  isNA(value) ? 'N/A' : renderUpper(String(value).replace(/-/g, ' '))

// The image lives in a fixed-size box that starts in the isLoading state; the
// img's own onload clears it. Reserving the space keeps the row height stable
// while the file is in flight, so the list no longer steps up and down as a
// page's images arrive one at a time. onerror swaps in the placeholder and lets
// its load event clear isLoading in turn.
const renderImage = img => `
  <div class="${styles.imageColumn}">
    <div class="${styles.expandButton}">+</div>
    <div class="${styles.imageBox} ${styles.isLoading}">
      <img
        src="${imageUrl('inventory', img)}"
        onload="this.parentNode.classList.remove('${styles.isLoading}');"
        onerror="this.onerror=null; this.src='${defaultDiamondImage}';"
      />
    </div>
  </div>
`

const isDifferentPage = (currentProps, nextProps) =>
  currentProps.location.query.page !== nextProps.location.query.page

const settingFilterNullItem = {
  value: null,
  text: 'All Settings/Jewelry'
}

const settingFilterItems = [
  {
    value: 'loose',
    text: 'Loose Diamonds'
  },
  {
    value: 'gemstone',
    text: 'Gemstones'
  },
  {
    value: 'mounted',
    text: 'Mounted Jewelry'
  },
  {
    value: 'non-diamond-jewelry',
    text: 'Non-Diamond Jewelry'
  },
  {
    value: 'craft-jewelry',
    text: 'Craft Jewelry'
  },
  {
    value: 'service',
    text: 'Service'
  }
].concat(
  Object.keys(jewelryTypes).map(value => ({
    value,
    text: jewelryTypes[value]
  }))
)

class DiamondList extends Component {
  constructor(props) {
    super(props)
    this.state = {
      selectedSetting: settingFilterNullItem
    }
  }
  componentDidMount() {
    const {
      isAdminUser,
      retailer,
      currentUser,
      getRetailer,
      clearSelectedDiamond
    } = this.props
    if (!isAdminUser && !retailer && currentUser) {
      const id = typeof currentUser.get === 'function'
        ? currentUser.get('id')
        : currentUser.id
      getRetailer(id)
    }
    clearSelectedDiamond()
    // The add/edit forms redirect here with ?saved=<key> so the confirmation
    // survives the navigation (this page owns the ToastContainer).
    showSavedMessage(
      {
        created: 'Item added successfully!',
        updated: 'Item updated successfully!'
      },
      this.props.location.search
    )
  }
  componentDidUpdate(prevProps) {
    const { router, location, diamonds, diamondInProgress } = this.props
    if (
      !diamonds ||
      isDifferentPage(prevProps, this.props) ||
      diamondInProgress
    ) {
      return
    }
    const nextPage = location.query.page
    const page = diamonds.getMeta('pageNumber')
    // Coerce both to strings before comparing — the Laravel API returns
    // current_page as a number while location.query.page is a string, which
    // would otherwise always differ ("1" !== 1) and trigger an infinite
    // router.replace loop in componentDidUpdate.
    if (String(nextPage) !== String(page)) {
      const newLocation = {
        pathname: location.pathname,
        query: Object.assign({}, location.query, { page })
      }
      if (location.query.page) {
        router.push(newLocation)
      } else {
        router.replace(newLocation)
      }
    }
  }
  render() {
    const {
      location,
      isAdminUser,
      retailer,
      diamonds,
      diamondInProgress,
      fetchDiamonds
    } = this.props
    if (!isAdminUser && !retailer && !location.query.page) {
      return null
    }
    let page = parseInt(location.query.page)
    page = _.isInteger(page) ? Math.max(page, 1) : 1
    const displayStart = (page - 1) * LIST_PAGE_LENGTH
    const columns = [
      { data: 'firstImagePath', render: renderImage },
      { data: 'retailerName', isHidden: !isAdminUser },
      { data: 'statusCode', render: createStatusBadgeString },
      { data: 'price' },
      { data: 'currency', className: styles.currencyColumn },
      { data: 'setting', render: this.renderSetting, className: styles.settingColumn },
      { data: 'shape', render: renderTitle },
      { data: 'carat', render: renderCarat, className: styles.caratColumn },
      { data: 'cut', render: renderTitle, className: styles.cutColumn },
      { data: 'clarity', render: renderClarity },
      { data: 'certificate', render: renderUpper },
      { data: 'color', render: renderColor },
      { data: null, render: this.renderEditButtons }
    ].filter(col => !col.isHidden)
    return (
      <div id="content" className={styles.listWrapper}>
        <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} newestOnTop closeOnClick pauseOnHover />
        <div className="row">
          <BigBreadcrumbs
            items={['Inventory']}
            icon="table"
            className="col-xs-12 col-sm-7 col-md-7 col-lg-4"
          />
          {this.renderActionButtons()}
        </div>
        {diamonds &&
        diamonds.size() === 0 &&
        !diamonds.getMeta('query') &&
        !diamondInProgress
          ? <EmptyTableDiamonds />
          : null}
        <WidgetGrid>
          <div className="row">
            <article className="col-sm-12">
              <JarvisWidget
                editbutton={false}
                togglebutton={false}
                fullscreenbutton={false}
                deletebutton={false}
                color="darken"
              >
                <header>
                  <span className="widget-icon">
                    <i className="fa fa-list" />
                  </span>
                  <h2>Items</h2>
                </header>
                <div>
                  <div className="widget-body no-padding">
                    <Datatable
                      options={{
                        processing: true,
                        serverSide: true,
                        ajax: (query, callback) => {
                          fetchDiamonds(
                            this.state.selectedSetting,
                            query,
                            callback
                          )
                        },
                        lengthChange: false,
                        pageLength: LIST_PAGE_LENGTH,
                        displayStart,
                        search: {
                          search: location.query.query
                        },
                        columns,
                        autoWidth: false,
                        responsive: { details: { type: 'inline' } },
                      }}
                       searchPlaceholder={`Retailer or Setting`}
                      className={classNames(
                        'table table-striped table-bordered table-hover',
                        styles.table
                      )}
                      width="100%"
                      ref={node => {
                        this.cachedDatatable = node && node.cachedDatatable
                      }}
                    >
                      <thead>
                        <tr>
                          <th className="all">Image</th>
                          {isAdminUser && <th>Retailer</th>}
                          <th>Status</th>
                          <th>Price</th>
                          <th className={styles.currencyColumn}>Currency</th>
                          <th className={styles.settingColumn}>Setting</th>
                          <th>Shape</th>
                          <th className={styles.caratColumn}>Carat</th>
                          <th className={styles.cutColumn}>Cut</th>
                          <th>Clarity</th>
                          <th>Certs</th>
                          <th>Color</th>
                          <th data-priority="1">Actions</th>
                        </tr>
                      </thead>
                    </Datatable>
                    {this.renderSettingFilter()}
                  </div>
                </div>
              </JarvisWidget>
            </article>
            <div className={styles.sectionHelp}>
              <i className="fa fa-question-circle" />
              <div className={styles.sectionHelpBlurb}>
                <span>
                  <a
                    href="https://intercom.help/gemsby/selling-on-gemsby"
                    target="_blank"
                  >
                    Gemsby Help Center
                  </a>
                </span>
                <a
                  href="https://intercom.help/gemsby/selling-on-gemsby/preparing-your-store/tips-on-preparing-your-store"
                  target="_blank"
                >
                  <strong>Tips on Preparing Your Store</strong>
                </a>
              </div>
            </div>
          </div>
        </WidgetGrid>
      </div>
    )
  }
  renderActionButtons() {
    const { router, isAdminUser, retailer, diamonds } = this.props
    if (!diamonds) {
      return null
    }
    if (isAdminUser || isStandardRetailer(retailer)) {
      return (
        <div className={styles.actionButtons}>
          <button
            className={`btn btn-primary ${styles.actionButton}`}
            onClick={() => {
              router.push('/inventory/csvUpload')
            }}
          >
            <i className="fa fa-upload" style={{ marginRight: 6 }} />
            Bulk Upload (CSV File)
          </button>
          {this.renderAddButton()}
        </div>
      )
    }
    if (diamonds.data.length < 2) {
      return (
        <div className={styles.actionButtons}>
          {this.renderAddButton()}
        </div>
      )
    }
    return (
      <div className={styles.actionButtons}>
        <div className={styles.upgradeMessage}>
          You have reached the account limit, upgrade to{' '}
          <strong>Standard Account</strong> to upload more inventory.
        </div>
        <UpgradeAccountButton className={styles.actionButton} />
      </div>
    )
  }
  renderAddButton() {
    return (
      <button
        className={`btn btn-primary ${styles.actionButton}`}
        onClick={() => {
          this.props.router.push('/inventory/new')
        }}
      >
        Add an Item
      </button>
    )
  }
  renderSettingFilter() {
    return (
      <div className={styles.settingFilter}>
        <div className={classNames('input-group', styles.settingFilterField)}>
          <span className="input-group-addon input-sm">
            <i className="glyphicon glyphicon-filter" />
          </span>
          <div
            className={classNames(
              'form-control',
              'input-sm',
              styles.settingFilterDropdown
            )}
          >
            <button
              type="button"
              className="dropdown-toggle"
              data-toggle="dropdown"
            >
              <span>
                {this.state.selectedSetting.text}
              </span>
              <span className="caret" />
            </button>
            <ul className="dropdown-menu">
              {settingFilterItems.map(item =>
                <li key={item.value}>
                  <a href="#" onClick={this.onFilterSelect(item)}>
                    {item.text}
                  </a>
                </li>
              )}
            </ul>
          </div>
        </div>
        {this.state.selectedSetting.value &&
          <ClearFormFieldButton onClick={this.onFilterClear} />}
      </div>
    )
  }
  renderSetting = (setting, type, row) => {
    if (setting === 'mounted' || setting === 'non-diamond-jewelry') {
      const jewelryType = jewelryTypes[row.jewelryType]
      if (jewelryType) {
        return jewelryType
      }
    }
    return settings[setting] || setting
  }
  renderEditButtons = ({ id, statusCode }) => {
    const { location, diamonds } = this.props
    // Build the back-URL from the current location, injecting the active query string
    const query = Object.assign({}, location.query)
    if (diamonds) {
      const metaQuery = diamonds.getMeta('query')
      if (metaQuery) query.query = metaQuery
    }
    const qs = new URLSearchParams(query).toString()
    const backUrl = encodeURIComponent(location.pathname + (qs ? `?${qs}` : ''))
    id = encodeURIComponent(id)
    const liveLink =
      statusCode === statusCodes.DELETED.value
        ? ''
        : `<a href="${SERVER}/diamond/${id}" target="_blank">
           <button class="btn btn-primary" style="margin: 4px;">
             Live Link
           </button>
         </a>`
    
    const editLink = statusCode === statusCodes.DELETED.value
      ? ''
      : `<a href="/inventory/${id}/edit?backUrl=${backUrl}">
          <button class="btn btn-primary" style="margin: 4px;">
            Edit
          </button>
      </a>`
    return `<div>
      ${editLink}
      ${liveLink}
    </div>`
  }
  setSettingFilter(item) {
    this.setState({ selectedSetting: item })
    setTimeout(() => {
      this.cachedDatatable.search('').draw()
    })
  }
  onFilterSelect = item => {
    return event => {
      event.preventDefault()
      if (!this.cachedDatatable) {
        return
      }
      this.setSettingFilter(item)
    }
  }
  onFilterClear = event => {
    event.preventDefault()
    if (!this.cachedDatatable) {
      return
    }
    this.props.clearDiamonds()
    this.setSettingFilter(settingFilterNullItem)
  }
}

const mapStateToProps = ({ retailer, diamond, login }) => {
  return {
    retailer: retailer.get('selectedRetailer'),
    diamonds: diamond.get('diamonds'),
    diamondInProgress: diamond.get('inProgress'),
    isAdminUser: login.get('isAdminUser'),
    currentUser: login.get('currentUser')
  }
}

const mapDispatchToProps = dispatch => {
  return {
    fetchDiamonds(selectedSetting, query, callback) {
      // The API takes the dropdown value unchanged and works out whether it
      // names a setting or a jewelry type. The Algolia-shaped split below is
      // kept because the other diamond lists still rely on it.
      query.selectedFilter = selectedSetting.value
      query.setting = selectedSetting.value
      if (query.setting && query.setting !== 'loose') {
        query.setting = 'mounted'
        query.jewelryType = selectedSetting.value
      }
      dispatch(
        fetchDiamonds(query, diamonds => {
          setTimeout(() => {
            try {
              if (!diamonds) {
                callback({ recordsTotal: 0, recordsFiltered: 0, data: [] })
                return
              }
              const recordsTotal = diamonds.getMeta('recordsTotal') || 0
              callback({
                recordsTotal,
                recordsFiltered: recordsTotal,
                data: (diamonds.toJSON() || [])
                  .map(item => {
                    const attrs = item.attributes || {}
                    return _.extend({}, attrs, {
                      id: item.id,
                      uuid: attrs.uuid || attrs.objectID || attrs.internalId || '',
                    })
                  })
              })
            } catch (e) {
              console.error('DataTable fetch error:', e)
              callback({ recordsTotal: 0, recordsFiltered: 0, data: [] })
            }
          })
        }, { liveSellerOnly: true })
      )
    },
    getRetailer(retailerId) {
      dispatch(getRetailer(retailerId))
    },
    clearDiamonds() {
      dispatch(clearDiamonds())
    },
    clearSelectedDiamond() {
      dispatch(clearSelectedDiamond())
    }
  }
}

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(DiamondList)
)
